<div class="content-account-settings animate" style="max-width: 560px">

    <!-- Loading -->
    <div *ngIf="isLoading" class="flex justify-center items-center py-20">
        <msc-common-spinner></msc-common-spinner>
    </div>

    <!-- No subscription -->
    <div *ngIf="!isLoading && !subscription" class="block-settings">
        <div class="block-settings__content flex flex-col items-center py-8 text-gray-400">
            <i class="icon icon-credit-card text-5xl mb-4 text-gray-300"></i>
            <p class="text-base text-gray-500">{{ 'lms-subscription.no-subscription' | translate }}</p>
        </div>
    </div>

    <!-- Subscription card -->
    <ng-container *ngIf="!isLoading && subscription as sub">
        <div class="block-settings">
            <div class="block-settings__content">

                <!-- Plan name + status badge -->
                <div class="flex items-center justify-between mb-6">
                    <div>
                        <h2 class="text-xl font-semibold text-gray-800">{{ sub.plan?.name }}</h2>
                        <p class="text-sm text-gray-400 mt-1" *ngIf="sub.plan?.price > 0">
                            {{ sub.plan?.price | number:'1.2-2' }} {{ sub.plan?.currency }}
                        </p>
                    </div>

                    <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                        [ngClass]="{
                            'bg-green-100 text-green-700': sub.status === 'active' && !isPendingCancellation,
                            'bg-blue-100 text-blue-700': sub.status === 'trial',
                            'bg-yellow-100 text-yellow-700': sub.status === 'pastDue',
                            'bg-orange-100 text-orange-700': isPendingCancellation,
                            'bg-purple-100 text-purple-700': isLifetime,
                            'bg-gray-100 text-gray-600': sub.status === 'cancelled' || sub.status === 'expired'
                        }">
                        <ng-container *ngIf="isLifetime">{{ 'lms-subscription.lifetime' | translate }}</ng-container>
                        <ng-container *ngIf="!isLifetime && isPendingCancellation">{{ 'lms-subscription.cancellation-scheduled' | translate }}</ng-container>
                        <ng-container *ngIf="!isLifetime && !isPendingCancellation && sub.status === 'active'">{{ 'lms-subscription.active' | translate }}</ng-container>
                        <ng-container *ngIf="sub.status === 'trial'">{{ 'lms-subscription.trial' | translate }}</ng-container>
                        <ng-container *ngIf="sub.status === 'pastDue'">{{ 'lms-subscription.past-due' | translate }}</ng-container>
                        <ng-container *ngIf="sub.status === 'cancelled'">{{ 'lms-subscription.cancelled' | translate }}</ng-container>
                    </span>
                </div>

                <!-- Date rows -->
                <div class="mb-6">
                    <div class="flex justify-between items-center py-3 border-b border-gray-100"
                        *ngIf="sub.currentPeriodStart">
                        <span class="text-sm text-gray-500">{{ 'words.start-date' | translate }}</span>
                        <span class="text-sm font-medium text-gray-700">{{ sub.currentPeriodStart | date:'mediumDate' }}</span>
                    </div>

                    <div class="flex justify-between items-center py-3 border-b border-gray-100"
                        *ngIf="sub.currentPeriodEnd && !isLifetime">
                        <span class="text-sm text-gray-500">
                            <ng-container *ngIf="isPendingCancellation">{{ 'lms-subscription.access-until' | translate }}</ng-container>
                            <ng-container *ngIf="!isPendingCancellation && sub.nextPaymentDate">{{ 'lms-subscription.renews-on' | translate }}</ng-container>
                            <ng-container *ngIf="!isPendingCancellation && !sub.nextPaymentDate">{{ 'lms-subscription.access-until' | translate }}</ng-container>
                        </span>
                        <span class="text-sm font-medium text-gray-700">{{ sub.currentPeriodEnd | date:'mediumDate' }}</span>
                    </div>

                    <div class="flex justify-between items-center py-3 border-b border-gray-100"
                        *ngIf="sub.trialEndsAt && sub.status === 'trial'">
                        <span class="text-sm text-gray-500">{{ 'words.trial-ends' | translate }}</span>
                        <span class="text-sm font-medium text-gray-700">{{ sub.trialEndsAt | date:'mediumDate' }}</span>
                    </div>
                </div>

                <!-- Past due warning -->
                <div *ngIf="sub.status === 'pastDue'"
                    class="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-6">
                    <i class="icon icon-alert-triangle text-yellow-500"></i>
                    <p class="text-sm text-yellow-700">{{ 'lms-subscription.past-due-warning' | translate }}</p>
                </div>

                <!-- Pending cancellation info -->
                <div *ngIf="isPendingCancellation && sub.currentPeriodEnd"
                    class="flex items-center gap-3 p-4 bg-orange-50 border border-orange-200 rounded-lg mb-6">
                    <i class="icon icon-info text-orange-500"></i>
                    <p class="text-sm text-orange-700">
                        {{ 'lms-subscription.cancellation-info' | translate }}
                        <strong>{{ sub.currentPeriodEnd | date:'mediumDate' }}</strong>.
                    </p>
                </div>

                <!-- Cancel button -->
                <div *ngIf="showCancelButton" class="flex justify-end pt-2">
                    <msc-button palette="secondary" (click)="openCancelModal()">
                        {{ 'lms-subscription.cancel-title' | translate }}
                    </msc-button>
                </div>

            </div>
        </div>
    </ng-container>

</div>

<!-- Cancel confirmation modal -->
<msc-common-modal size="40vw" #cancelModal [title]="'lms-subscription.cancel-title' | translate">
    <ng-template>
        <p class="text-sm text-gray-600 mb-4">{{ 'lms-subscription.cancel-description' | translate }}</p>
        <p class="text-sm text-gray-500 mb-1">{{ 'lms-subscription.cancel-reason' | translate }}</p>
        <textarea
            class="w-full border border-gray-200 rounded-lg p-3 text-sm text-gray-700 resize-none focus:outline-none focus:ring-2 focus:ring-blue-300"
            rows="3"
            [(ngModel)]="cancelReason">
        </textarea>
        <div class="flex items-center mt-5 space-x-3 justify-end">
            <msc-button palette="secondary" (click)="cancelModal.onClose()">{{ 'common.cancel' | translate }}</msc-button>
            <msc-button palette="primary" [disabled]="isCancelling" (click)="confirmCancel()">
                {{ 'lms-subscription.cancel-confirm' | translate }}
            </msc-button>
        </div>
    </ng-template>
</msc-common-modal>
